import { Box, ButtonBase, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { forwardRef, MouseEvent, ReactNode } from "react";

const colors = {
    primary: "primary.main",
    success: "success.main",
    secondary: "text.secondary",
    inherit: "text.primary",
};

// TinyButton is a compact text button for inline controls and toolbars. It
// sizes itself to sit beside body text instead of standing out as a form
// control, and truncates its label rather than pushing its neighbours around.
export const TinyButton = forwardRef<HTMLButtonElement, {
    children?: ReactNode;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
    color?: "primary" | "success" | "secondary" | "inherit";
    variant?: "text" | "outlined";
    disabled?: boolean;
    width?: number;
    sx?: SxProps<Theme>;
    "aria-haspopup"?: "listbox" | "menu" | "dialog" | "true";
    "aria-expanded"?: boolean;
    "aria-label"?: string;
}>(function TinyButton(props, ref) {
    const color = props.disabled ? "text.disabled" : colors[props.color || "inherit"];

    return (
        <Typography
            ref={ref}
            variant="caption"
            component={ButtonBase}
            disableRipple
            disabled={props.disabled}
            onClick={props.onClick}
            aria-haspopup={props["aria-haspopup"]}
            aria-expanded={props["aria-expanded"]}
            aria-label={props["aria-label"]}
            sx={[
                {
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    textAlign: "left",
                    lineHeight: 1.4,
                    fontSize: 13,
                    padding: "2px 4px",
                    width: props.width,
                    borderRadius: 1,
                    // keep the border in both variants so switching variant doesn't shift layout
                    border: 1,
                    borderColor: props.variant === "outlined" ? color : "transparent",
                    color: color,
                    "&:hover": { bgcolor: "action.hover" },
                    "&:focus-visible": { bgcolor: "action.hover", outline: 1, outlineColor: "primary.main" },
                },
                ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
            ]}
        >
            {props.startIcon}
            <Box component="span" sx={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}>
                {props.children}
            </Box>
            {props.endIcon}
        </Typography>
    );
});
